import { DataTableActionBarAction } from '@/components/data-table/data-table-action-bar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import type { ActionConfig, OperationFilters } from '@/components/subscriptions/subscription-manager/types'
import { useExecuteOperation, usePreviewOperation } from '@/lib/api/subscriptionManager'
import { Info, Loader2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

// Charging on the processor already in use is a mode of the existing retry action, not a new one:
// no target processor, no counter reset, and no date move that would defeat the due-date gate.
const RETRY_PAYMENT_ACTION: ActionConfig = {
    type: 'retry_new_processor',
    retryMode: 'charge_now',
    resetRetryCounter: false,
    billImmediately: false,
}

interface RetryPaymentBulkActionModalProps {
    subscriptionIds: string[]
}

export const RetryPaymentBulkActionModal = ({ subscriptionIds }: RetryPaymentBulkActionModalProps) => {
    const { t } = useTranslation('subscriptions')
    const { mutate: executeOperation, isPending } = useExecuteOperation()
    const [isOpen, setIsOpen] = useState(false)

    const filters: OperationFilters = { subscriptionIds }
    const { data: preview, isLoading: isPreviewLoading } = usePreviewOperation(
        filters,
        RETRY_PAYMENT_ACTION,
        isOpen,
    )

    const handleSubmit = () => {
        executeOperation(
            { filters, action: RETRY_PAYMENT_ACTION, subscriptionIds },
            { onSettled: () => setIsOpen(false) },
        )
    }

    const handleOpenChange = (open: boolean) => {
        if (!open && !isPending) setIsOpen(false)
    }

    return (
        <>
            <DataTableActionBarAction
                tooltip={t('bulkActions.retryPayment.tooltip')}
                isPending={isPending}
                onClick={() => setIsOpen(true)}
            >
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('bulkActions.retryPayment.button')}
            </DataTableActionBarAction>
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent className="sm:max-w-[460px]">
                    <DialogHeader>
                        <DialogTitle>{t('bulkActions.retryPayment.title')}</DialogTitle>
                        <DialogDescription>
                            {t('bulkActions.retryPayment.confirmMessage', { count: subscriptionIds.length })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {isPreviewLoading && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="size-4 animate-spin" />
                                {t('bulkActions.retryPayment.checking')}
                            </div>
                        )}
                        {preview && (
                            <p className="text-sm">
                                {t('bulkActions.retryPayment.eligible', {
                                    count: preview.compatibleCount,
                                    total: subscriptionIds.length,
                                })}
                            </p>
                        )}
                        <Alert variant="warning">
                            <Info />
                            <AlertDescription>{t('bulkActions.retryPayment.asyncNotice')}</AlertDescription>
                        </Alert>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setIsOpen(false)} variant="outline">
                            {t('bulkActions.retryPayment.cancel')}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={isPending || isPreviewLoading || preview?.compatibleCount === 0}
                        >
                            {isPending && <Loader2 className="size-4 animate-spin mr-2" />}
                            {t('bulkActions.retryPayment.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
